import { useState, useEffect, useRef } from 'react'
import type {
  LabClient,
  Salesperson,
  LabClientPdfTemplate,
  PrinterResponse,
  LabelTemplateResponse,
} from '@alpha-edge/shared'
import { PDF_LAYOUT_VALUES } from '@alpha-edge/shared'
import type { PdfLayout } from '@alpha-edge/shared'
import {
  Modal,
  FormField,
  btnPrimary,
  btnSecondary,
  btnDanger,
  colors,
  input,
  ConfirmDialog,
} from '../../components/ui'
import { useModal } from '../../hooks/use-modal'
import { api, ApiError } from '../../services/api'

interface LabClientForm {
  code: string
  name: string
  barcodePrefix: string
  contactName: string
  contactEmail: string
  contactPhone: string
  street: string
  city: string
  state: string
  zip: string
  country: string
  printerId: string
  defaultLabelTemplateId: string
  salespersonId: string
  notes: string
}

interface PdfTemplateForm {
  layout: PdfLayout
  accentColor: string
  pageSize: 'letter' | 'a4'
  footerText: string
  complianceFields: { clia: boolean; npi: boolean; labDirector: boolean }
  headerLogoStorageRef: string
  // §7.6.21 Slice 4 -- requisition template toggles
  includeEligibilityQuestion: boolean
  includePresumptivePanel: boolean
  separateTestsOnReq: boolean
  reqTestSelectionMode: 'ordered_only' | 'all'
  reqEnabled: boolean
}

const UNSHIPPED_LAYOUTS = new Set<PdfLayout>(['compact'])

const defaultPdfTemplate: PdfTemplateForm = {
  layout: 'standard',
  accentColor: '',
  pageSize: 'letter',
  footerText: '',
  complianceFields: { clia: true, npi: true, labDirector: true },
  headerLogoStorageRef: '',
  includeEligibilityQuestion: false,
  includePresumptivePanel: false,
  separateTestsOnReq: false,
  reqTestSelectionMode: 'all',
  reqEnabled: true,
}

const defaults: LabClientForm = {
  code: '',
  name: '',
  barcodePrefix: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  street: '',
  city: '',
  state: '',
  zip: '',
  country: 'US',
  printerId: '',
  defaultLabelTemplateId: '',
  salespersonId: '',
  notes: '',
}

interface LabClientEditModalProps {
  readonly labClientId: string | null
  readonly isOpen: boolean
  readonly isNew: boolean
  readonly onClose: () => void
  readonly onSaved: (client: LabClient) => void
  readonly onArchived?: (clientId: string) => void
}

function clientToForm(c: LabClient): LabClientForm {
  return {
    code: c.code,
    name: c.name,
    barcodePrefix: c.barcodePrefix,
    contactName: c.contactName ?? '',
    contactEmail: c.contactEmail ?? '',
    contactPhone: c.contactPhone ?? '',
    street: c.street ?? '',
    city: c.city ?? '',
    state: c.state ?? '',
    zip: c.zip ?? '',
    country: c.country ?? 'US',
    printerId: c.printerId ?? '',
    defaultLabelTemplateId: c.defaultLabelTemplateId ?? '',
    salespersonId: c.salespersonId ?? '',
    notes: c.notes ?? '',
  }
}

function clientToPdfTemplateForm(c: LabClient): PdfTemplateForm {
  const t = c.pdfTemplate
  if (!t) return defaultPdfTemplate
  return {
    layout: t.layout ?? 'standard',
    accentColor: t.accentColor ?? '',
    pageSize: t.pageSize ?? 'letter',
    footerText: t.footerText ?? '',
    complianceFields: {
      clia: t.complianceFields?.clia ?? true,
      npi: t.complianceFields?.npi ?? true,
      labDirector: t.complianceFields?.labDirector ?? true,
    },
    headerLogoStorageRef: t.headerLogoStorageRef ?? '',
    includeEligibilityQuestion: t.includeEligibilityQuestion ?? false,
    includePresumptivePanel: t.includePresumptivePanel ?? false,
    separateTestsOnReq: t.separateTestsOnReq ?? false,
    reqTestSelectionMode: t.reqTestSelectionMode ?? 'all',
    reqEnabled: t.reqEnabled ?? true,
  }
}

function pdfTemplateFormToPayload(t: PdfTemplateForm): LabClientPdfTemplate {
  return {
    layout: t.layout,
    accentColor: t.accentColor || undefined,
    pageSize: t.pageSize,
    footerText: t.footerText || undefined,
    complianceFields: t.complianceFields,
    headerLogoStorageRef: t.headerLogoStorageRef || undefined,
    includeEligibilityQuestion: t.includeEligibilityQuestion,
    includePresumptivePanel: t.includePresumptivePanel,
    separateTestsOnReq: t.separateTestsOnReq,
    reqTestSelectionMode: t.reqTestSelectionMode,
    reqEnabled: t.reqEnabled,
  }
}

export function LabClientEditModal({
  labClientId,
  isOpen,
  isNew,
  onClose,
  onSaved,
  onArchived,
}: LabClientEditModalProps) {
  const [form, setForm] = useState<LabClientForm>(defaults)
  const [pdfTemplate, setPdfTemplate] = useState<PdfTemplateForm>(defaultPdfTemplate)
  const [salespersons, setSalespersons] = useState<readonly Salesperson[]>([])
  const [printers, setPrinters] = useState<readonly PrinterResponse[]>([])
  const [labelTemplates, setLabelTemplates] = useState<readonly LabelTemplateResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isSystem, setIsSystem] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const archiveModal = useModal()
  const [archiving, setArchiving] = useState(false)
  const prevUrlRef = useRef<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    api
      .get<readonly Salesperson[]>('/salespersons?isActive=true&limit=100')
      .then((res) => {
        if (cancelled) return
        if (res.data) setSalespersons(res.data)
      })
      .catch(() => {
        // non-fatal -- salespersons list is optional decoration
      })
    return () => {
      cancelled = true
    }
  }, [isOpen])

  // Load active printers for the org
  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    api
      .get<readonly PrinterResponse[]>('/admin/printers?limit=100')
      .then((res) => {
        if (cancelled) return
        if (res.data) setPrinters(res.data.filter((p) => !p.isArchived))
      })
      .catch(() => {
        // non-fatal
      })
    return () => {
      cancelled = true
    }
  }, [isOpen])

  // Load label templates filtered by the currently selected printer's driver
  const selectedPrinter = printers.find((p) => p.id === form.printerId)
  const selectedPrinterDriver = selectedPrinter?.driver
  useEffect(() => {
    if (!isOpen) return
    if (!selectedPrinterDriver) {
      setLabelTemplates([])
      return
    }
    let cancelled = false
    api
      .get<readonly LabelTemplateResponse[]>(`/admin/label-templates?driver=${selectedPrinterDriver}&limit=100`)
      .then((res) => {
        if (cancelled) return
        if (res.data) setLabelTemplates(res.data.filter((t) => !t.isArchived))
      })
      .catch(() => {
        // non-fatal
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, selectedPrinterDriver])

  useEffect(() => {
    if (!isOpen) return
    if (isNew) {
      setForm(defaults)
      setPdfTemplate(defaultPdfTemplate)
      setError(null)
      setIsSystem(false)
      return
    }
    if (!labClientId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    api
      .get<LabClient>(`/lab-clients/${labClientId}`)
      .then((res) => {
        if (cancelled) return
        if (res.data) {
          setForm(clientToForm(res.data))
          setPdfTemplate(clientToPdfTemplateForm(res.data))
          setIsSystem(res.data.isSystem)
        }
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.message : 'Failed to load lab client')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [labClientId, isOpen, isNew])

  // Revoke previous object URL on each new preview to avoid leaks
  useEffect(() => {
    return () => {
      if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current)
    }
  }, [previewUrl])

  const update = <K extends keyof LabClientForm>(key: K, value: LabClientForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  const updatePdf = <K extends keyof PdfTemplateForm>(key: K, value: PdfTemplateForm[K]) =>
    setPdfTemplate((prev) => ({ ...prev, [key]: value }))

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      const payload: Record<string, unknown> = { ...form }
      for (const key of [
        'contactName',
        'contactEmail',
        'contactPhone',
        'street',
        'city',
        'state',
        'zip',
        'printerId',
        'defaultLabelTemplateId',
        'salespersonId',
        'notes',
      ] as const) {
        if (payload[key] === '') delete payload[key]
      }
      // barcodePrefix is immutable on update; let the backend reject if sent
      if (!isNew) delete (payload as Record<string, unknown>).barcodePrefix
      payload.pdfTemplate = pdfTemplateFormToPayload(pdfTemplate)
      const res = isNew
        ? await api.post<LabClient>('/lab-clients', payload)
        : await api.put<LabClient>(`/lab-clients/${labClientId}`, payload)
      if (!res.data) throw new Error('No data returned')
      onSaved(res.data)
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function handleArchive() {
    if (!labClientId) return
    setArchiving(true)
    try {
      await api.delete(`/lab-clients/${labClientId}`)
      onArchived?.(labClientId)
      archiveModal.close()
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to archive')
    } finally {
      setArchiving(false)
    }
  }

  async function handlePreview() {
    setPreviewing(true)
    setError(null)
    try {
      const res = await fetch('/api/results/preview-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` },
        body: JSON.stringify({ template: pdfTemplateFormToPayload(pdfTemplate) }),
      })
      if (!res.ok) {
        const body = (await res.json()) as { error?: string }
        throw new Error(body.error ?? `Preview failed (${res.status})`)
      }
      const blob = await res.blob()
      if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current)
      const url = URL.createObjectURL(blob)
      prevUrlRef.current = url
      setPreviewUrl(url)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Preview failed')
    } finally {
      setPreviewing(false)
    }
  }

  async function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 2 * 1024 * 1024) {
      setError('Logo must be under 2 MB')
      return
    }
    const formData = new FormData()
    formData.append('file', file)
    try {
      const res = await api.postForm<{ path: string }>('/files/upload', formData)
      if (res.data?.path) updatePdf('headerLogoStorageRef', res.data.path)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Logo upload failed')
    }
  }

  const title = isNew ? 'New Lab Client' : `${form.code} - ${form.name}`

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={title} width="780px">
        {loading ? (
          <div style={{ color: colors.textMuted, padding: '1rem 0' }}>Loading...</div>
        ) : (
          <div>
            {isSystem && (
              <div
                style={{
                  padding: '0.625rem 0.875rem',
                  marginBottom: '1rem',
                  background: '#fef3c7',
                  border: '1px solid #f59e0b',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  color: '#92400e',
                }}
                data-testid="system-record-notice"
              >
                This is a system-provided lab client and cannot be edited or archived.
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: '0.75rem' }}>
              <FormField
                name="code"
                label="Code"
                required
                value={form.code}
                onChange={(v) => update('code', v)}
                disabled={isSystem}
              />
              <FormField
                name="name"
                label="Name"
                required
                value={form.name}
                onChange={(v) => update('name', v)}
                disabled={isSystem}
              />
              <FormField
                name="barcodePrefix"
                label="Barcode Prefix"
                required
                value={form.barcodePrefix}
                onChange={(v) => update('barcodePrefix', v.toUpperCase())}
                disabled={!isNew || isSystem}
              />
            </div>

            <div
              style={{
                gridColumn: '1 / -1',
                borderTop: `1px solid ${colors.border}`,
                paddingTop: '0.75rem',
                marginTop: '0.75rem',
              }}
            >
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '0.9375rem', fontWeight: 600, color: colors.text }}>
                Contact
              </h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
              <FormField
                name="contactName"
                label="Contact Name"
                value={form.contactName}
                onChange={(v) => update('contactName', v)}
              />
              <FormField
                name="contactEmail"
                label="Contact Email"
                type="email"
                value={form.contactEmail}
                onChange={(v) => update('contactEmail', v)}
              />
              <FormField
                name="contactPhone"
                label="Contact Phone"
                value={form.contactPhone}
                onChange={(v) => update('contactPhone', v)}
              />
            </div>

            <div
              style={{
                gridColumn: '1 / -1',
                borderTop: `1px solid ${colors.border}`,
                paddingTop: '0.75rem',
                marginTop: '0.75rem',
              }}
            >
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '0.9375rem', fontWeight: 600, color: colors.text }}>
                Address
              </h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '0.75rem' }}>
              <FormField name="street" label="Street" value={form.street} onChange={(v) => update('street', v)} />
              <FormField name="city" label="City" value={form.city} onChange={(v) => update('city', v)} />
              <FormField name="state" label="State" value={form.state} onChange={(v) => update('state', v)} />
              <FormField name="zip" label="ZIP" value={form.zip} onChange={(v) => update('zip', v)} />
            </div>

            <div
              style={{
                gridColumn: '1 / -1',
                borderTop: `1px solid ${colors.border}`,
                paddingTop: '0.75rem',
                marginTop: '0.75rem',
              }}
            >
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '0.9375rem', fontWeight: 600, color: colors.text }}>
                Routing & Sales
              </h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <FormField name="printerId" label="Printer">
                <select
                  style={input}
                  value={form.printerId}
                  onChange={(e) => {
                    update('printerId', e.target.value)
                    // Clear template selection when printer changes
                    update('defaultLabelTemplateId', '')
                  }}
                  data-testid="printer-select"
                >
                  <option value="">-- None --</option>
                  {printers.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.driver === 'dymo_client' ? 'DYMO' : 'Zebra'}, {p.model})
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField name="salespersonId" label="Salesperson">
                <select
                  style={input}
                  value={form.salespersonId}
                  onChange={(e) => update('salespersonId', e.target.value)}
                >
                  <option value="">-- None --</option>
                  {salespersons.map((sp) => (
                    <option key={sp.id} value={sp.id}>
                      {sp.name}
                    </option>
                  ))}
                </select>
              </FormField>
            </div>

            <div style={{ marginTop: '0.75rem' }}>
              <FormField name="defaultLabelTemplateId" label="Default Label Template">
                <select
                  style={input}
                  value={form.defaultLabelTemplateId}
                  onChange={(e) => update('defaultLabelTemplateId', e.target.value)}
                  disabled={!form.printerId}
                  data-testid="label-template-select"
                >
                  <option value="">-- None --</option>
                  {labelTemplates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </FormField>
            </div>

            <div style={{ marginTop: '0.75rem' }}>
              <FormField name="notes" label="Notes">
                <textarea
                  style={{ ...input, minHeight: '60px', resize: 'vertical' }}
                  value={form.notes}
                  onChange={(e) => update('notes', e.target.value)}
                />
              </FormField>
            </div>

            {/* PDF Template fieldset */}
            <div
              style={{
                borderTop: `1px solid ${colors.border}`,
                paddingTop: '0.75rem',
                marginTop: '0.75rem',
              }}
            >
              <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.9375rem', fontWeight: 600, color: colors.text }}>
                PDF Template
              </h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <FormField name="pdfLayout" label="Layout">
                <select
                  style={input}
                  value={pdfTemplate.layout}
                  onChange={(e) => updatePdf('layout', e.target.value as PdfLayout)}
                  data-testid="pdf-layout-select"
                >
                  {PDF_LAYOUT_VALUES.map((layout) => (
                    <option
                      key={layout}
                      value={layout}
                      disabled={UNSHIPPED_LAYOUTS.has(layout)}
                      title={UNSHIPPED_LAYOUTS.has(layout) ? 'Ships in a later §5.10 slice' : undefined}
                    >
                      {layout}
                    </option>
                  ))}
                </select>
              </FormField>

              <FormField name="pdfPageSize" label="Page Size">
                <select
                  style={input}
                  value={pdfTemplate.pageSize}
                  onChange={(e) => updatePdf('pageSize', e.target.value as 'letter' | 'a4')}
                >
                  <option value="letter">Letter</option>
                  <option value="a4">A4</option>
                </select>
              </FormField>

              <FormField name="pdfAccentColor" label="Accent Color">
                <input
                  type="color"
                  id="pdfAccentColor"
                  value={pdfTemplate.accentColor || '#0074d4'}
                  onChange={(e) => updatePdf('accentColor', e.target.value)}
                  style={{ ...input, height: '36px', padding: '2px', cursor: 'pointer' }}
                />
              </FormField>

              <FormField name="pdfLogoFile" label="Header Logo (max 2 MB)">
                <input
                  type="file"
                  id="pdfLogoFile"
                  accept="image/png,image/jpeg,image/svg+xml"
                  onChange={handleLogoUpload}
                  style={input}
                />
              </FormField>
            </div>

            {pdfTemplate.headerLogoStorageRef && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: colors.textMuted }}>
                Current logo: <code>{pdfTemplate.headerLogoStorageRef}</code>
                <button
                  type="button"
                  style={{ ...btnSecondary, marginLeft: '0.75rem', fontSize: '0.8125rem' }}
                  onClick={() => updatePdf('headerLogoStorageRef', '')}
                >
                  Clear
                </button>
              </div>
            )}

            <div style={{ marginTop: '0.75rem' }}>
              <FormField name="pdfFooterText" label="Footer Text">
                <textarea
                  style={{ ...input, minHeight: '60px', resize: 'vertical' }}
                  value={pdfTemplate.footerText}
                  maxLength={1000}
                  onChange={(e) => updatePdf('footerText', e.target.value)}
                />
              </FormField>
            </div>

            <div style={{ marginTop: '0.5rem' }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 500, color: colors.text, marginBottom: '0.375rem' }}>
                Compliance Fields
              </div>
              <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
                {(
                  [
                    { key: 'clia', label: 'CLIA' },
                    { key: 'npi', label: 'NPI' },
                    { key: 'labDirector', label: 'Lab Director' },
                  ] as const
                ).map(({ key, label }) => (
                  <label
                    key={key}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.875rem' }}
                  >
                    <input
                      type="checkbox"
                      checked={pdfTemplate.complianceFields[key]}
                      onChange={(e) =>
                        setPdfTemplate((prev) => ({
                          ...prev,
                          complianceFields: { ...prev.complianceFields, [key]: e.target.checked },
                        }))
                      }
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            <div style={{ marginTop: '0.75rem' }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 500, color: colors.text, marginBottom: '0.375rem' }}>
                Requisition Options
              </div>
              <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', alignItems: 'center' }}>
                {(
                  [
                    { key: 'reqEnabled', label: 'Requisition Enabled' },
                    { key: 'includeEligibilityQuestion', label: 'Eligibility Question' },
                    { key: 'includePresumptivePanel', label: 'Presumptive Panel' },
                    { key: 'separateTestsOnReq', label: 'Separate Page Per Test' },
                  ] as const
                ).map(({ key, label }) => (
                  <label
                    key={key}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.875rem' }}
                  >
                    <input
                      type="checkbox"
                      checked={pdfTemplate[key]}
                      onChange={(e) => updatePdf(key, e.target.checked)}
                    />
                    {label}
                  </label>
                ))}
                <FormField name="reqTestSelectionMode" label="Test Selection">
                  <select
                    style={input}
                    value={pdfTemplate.reqTestSelectionMode}
                    onChange={(e) => updatePdf('reqTestSelectionMode', e.target.value as 'ordered_only' | 'all')}
                  >
                    <option value="all">All (client test menu)</option>
                    <option value="ordered_only">Ordered tests only</option>
                  </select>
                </FormField>
              </div>
            </div>

            <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
              <button
                type="button"
                style={btnSecondary}
                onClick={handlePreview}
                disabled={previewing}
                data-testid="pdf-preview-btn"
              >
                {previewing ? 'Generating...' : 'Preview PDF'}
              </button>
            </div>

            {previewUrl && (
              <div style={{ marginTop: '0.75rem', border: `1px solid ${colors.border}`, borderRadius: '3px' }}>
                <iframe
                  src={previewUrl}
                  title="PDF Preview"
                  style={{ width: '100%', height: '520px', border: 'none', display: 'block' }}
                />
              </div>
            )}

            {error && (
              <div
                style={{
                  marginTop: '0.75rem',
                  color: colors.danger,
                  padding: '0.5rem 0.75rem',
                  background: colors.bgError,
                  border: `1px solid ${colors.borderError}`,
                  borderRadius: '3px',
                  fontSize: '0.875rem',
                }}
              >
                {error}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
              {!isNew && !isSystem && onArchived && (
                <button
                  type="button"
                  onClick={archiveModal.open}
                  style={{ ...btnDanger, marginRight: 'auto' }}
                  disabled={saving || archiving}
                >
                  Archive
                </button>
              )}
              <button type="button" onClick={onClose} style={btnSecondary} disabled={saving}>
                {isSystem ? 'Close' : 'Cancel'}
              </button>
              {!isSystem && (
                <button type="button" onClick={handleSave} style={btnPrimary} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={archiveModal.isOpen}
        onClose={archiveModal.close}
        onConfirm={handleArchive}
        title="Archive Lab Client"
        message={`Archive ${form.name}? They will be excluded from new orders but historical data is preserved.`}
        confirmLabel="Archive"
        loading={archiving}
      />
    </>
  )
}
